import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../hooks/apiRequest";
import { TaskProps, TaskStatus } from "../types/interfaces";
import { taskKeys } from "../utils/queryKeys";

interface TaskFilters {
  projectId: string;
  status?: TaskStatus;
  sprintId?: string;
  epicId?: string;
  assigneeId?: string;
}

function toQueryString(filters: TaskFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  return params.toString();
}

export function useTasks(filters: TaskFilters) {
  return useQuery({
    queryKey: taskKeys.list({ ...filters }),
    queryFn: async () => (await apiRequest<TaskProps[]>(`tasks?${toQueryString(filters)}`)).data,
    enabled: !!filters.projectId,
  });
}

export function useCreateTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<TaskProps> & { title: string }) =>
      (await apiRequest<TaskProps>("tasks", {
        method: "POST",
        body: JSON.stringify({ ...data, projectId }),
      })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taskKeys.list({ projectId }) }),
  });
}

export function useUpdateTask(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TaskProps> }) =>
      (await apiRequest<TaskProps>(`tasks/${id}`, { method: "PUT", body: JSON.stringify(data) })).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.list({ projectId }) });
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
    },
  });
}

export function useTransitionTaskStatus(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TaskStatus }) =>
      (await apiRequest<TaskProps>(`tasks/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      })).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.list({ projectId }) });
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
    },
  });
}

export function useTask(id: string) {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: async () => (await apiRequest<TaskProps>(`tasks/${id}`)).data,
    enabled: !!id,
  });
}
